<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { getLinkAttributes, type ROIMetricsBlock, ROI_DEFAULTS } from '../../types/roi'
import { useAnalytics } from '../../composables/useAnalytics'

/**
 * ROIBlock Component
 *
 * Displays ROI metrics in a responsive grid with substantiation links.
 * Handles internal/external link normalization and graceful degradation
 * for missing icons and empty data.
 *
 * Accessibility: WCAG 2.1 AA compliant with:
 * - Semantic heading hierarchy (h2 for sections, h3 for metric labels)
 * - ARIA labels on icons and interactive elements
 * - Keyboard navigation and visible focus states
 * - Proper color contrast (4.5:1 minimum text to background)
 *
 * Analytics: Emits roi_block_rendered on mount and substantiation_link_clicked on interaction.
 *
 * @component
 * @example
 * ```vue
 * <ROIBlock :data="roiMetricsBlock" product="Review" />
 * ```
 */

interface Props {
  /** ROI metrics block with normalized data from T001 helper */
  data: ROIMetricsBlock
  /** Product name for analytics context (e.g., "Review", "Verify", "Insight") */
  product?: string
}

const props = withDefaults(defineProps<Props>(), {
  product: 'unknown',
})

const { emitROIBlockRendered, emitSubstantiationLinkClicked } = useAnalytics()

/** Track if component has mounted for analytics */
const isMounted = ref(false)

/** Computed: Check if we have any metrics to render */
const hasMetrics = computed(() => props.data?.metrics?.length > 0)

/** Computed: Check if we have substantiation links to render */
const hasSubstantiation = computed(() => props.data?.substantiation?.length > 0)

/**
 * Get responsive grid classes based on metric count
 * Returns Nuxt UI grid classes for 1/2/3 column layout at breakpoints
 */
const gridClasses = computed(() => {
  const count = props.data?.metrics?.length || 0
  // Responsive: 1 col at mobile (375), 2 cols at tablet (768), 3 cols at desktop (1280)
  if (count === 1) {
    return 'grid grid-cols-1 gap-4'
  }
  if (count === 2) {
    return 'grid grid-cols-1 md:grid-cols-2 gap-4'
  }
  // 3+ metrics: full responsive grid
  return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
})

/**
 * Get fallback icon with proper name format for Heroicons
 * Returns icon name or fallback if missing
 */
const getIconName = (iconName?: string): string => {
  return iconName?.trim() || ROI_DEFAULTS.ICON_FALLBACK
}

/**
 * Handle substantiation link click with analytics emission
 * @param linkTitle - Link display text
 * @param linkUrl - Link target URL
 * @param linkType - Link type (internal/external)
 */
const handleLinkClick = (
  linkTitle: string,
  linkUrl: string,
  linkType: 'internal' | 'external'
): void => {
  emitSubstantiationLinkClicked(linkTitle, linkUrl, linkType, props.product)
}

/**
 * Emit analytics event when component mounts
 */
onMounted(() => {
  emitROIBlockRendered(props.product, hasMetrics.value ? props.data.metrics.length : 0, hasSubstantiation.value)
  isMounted.value = true
})
</script>

<template>
  <section
    class="space-y-8"
    aria-label="ROI metrics and substantiation"
    role="region"
  >
    <!-- Metrics Grid Section -->
    <div
      v-if="hasMetrics"
      class="space-y-4"
    >
      <h2 class="text-lg font-semibold text-gray-900">
        Key Metrics
      </h2>
      <div
        :class="gridClasses"
        role="list"
      >
        <UCard
          v-for="(metric, index) in data.metrics"
          :key="`metric-${index}`"
          role="listitem"
          class="flex flex-col focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 rounded-lg transition-all !bg-white !text-neutral-900 border border-neutral-200"
          :ui="{ body: { padding: 'p-4' } }"
        >
          <template #header>
            <div class="flex items-center gap-3">
              <UIcon
                :name="`i-heroicons-${getIconName(metric.icon)}`"
                class="w-5 h-5 text-primary-600 flex-shrink-0"
                :aria-label="`${metric.label} icon`"
              />
              <h3
                class="font-medium text-neutral-900 line-clamp-2"
              >
                {{ metric.label }}
              </h3>
            </div>
          </template>

          <!-- Metric Value and Unit -->
          <div class="space-y-1">
            <div class="flex items-baseline gap-1">
              <span
                class="text-2xl font-bold text-primary-600"
                aria-label="`${metric.label}: ${metric.value} ${metric.unit}`"
              >
                {{ metric.value }}
              </span>
              <span class="text-sm text-neutral-600">{{ metric.unit }}</span>
            </div>

            <!-- Optional Description -->
            <p
              v-if="metric.description"
              class="text-sm text-neutral-600"
            >
              {{ metric.description }}
            </p>
          </div>
        </UCard>
      </div>
    </div>

    <!-- Substantiation Section -->
    <div
      v-if="hasSubstantiation"
      class="space-y-4 border-t border-neutral-200 pt-8"
    >
      <h2 class="text-lg font-semibold text-neutral-900">
        How we calculate ROI
      </h2>

      <ul class="space-y-2">
        <li
          v-for="(link, index) in data.substantiation"
          :key="`link-${index}`"
          class="flex items-start gap-2"
        >
          <span
            class="text-primary-600 mt-1"
            aria-hidden="true"
          >
            •
          </span>
          <ULink
            :to="link.url"
            class="text-primary-600 hover:text-primary-700 hover:underline break-words focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded transition-all"
            :aria-label="`${link.title} (opens ${link.type} link)`"
            v-bind="getLinkAttributes(link)"
            @click="handleLinkClick(link.title, link.url, link.type || 'external')"
          >
            {{ link.title }}
          </ULink>
        </li>
      </ul>
    </div>

    <!-- Empty State Placeholder -->
    <div
      v-if="!hasMetrics && !hasSubstantiation"
      class="text-center py-12 border border-dashed border-neutral-300 rounded-lg bg-neutral-50"
      role="status"
      aria-label="Empty ROI block"
    >
      <p class="text-neutral-600">
        No ROI metrics available
      </p>
    </div>
  </section>
</template>

<style scoped>
/* Utility class for preventing CLS with line-clamp */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Focus state visible on keyboard navigation */
:focus-visible {
  outline: 2px solid rgb(34 197 94); /* green-500 */
  outline-offset: 2px;
}

/* High contrast for color-blind accessibility */
@media (prefers-contrast: more) {
  .text-primary-600 {
    --tw-text-opacity: 1;
    color: rgb(var(--color-primary-700) / var(--tw-text-opacity));
  }
}
</style>
